import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { UseOutletContextInvoice } from './Invoice';
import { StateLanguage } from '../../../../languages/config/StateLanguage';
import { InvoiceDA } from '../../../../services/admin/invoice.service';
import { Error } from '../../../../models/error.model';
import { GlobalSwal } from '../../../../styles/swal.style';
import { CompanyInvoices } from '../../../../models/company.invoices.model';
import { Invoice } from '../../../../models/invoice.model';
import { InvoiceGroup } from '../../../../models/invoice.group.model';
import { formatContact, formatMoney } from '../../../../scripts/format.value.script';
import { dateFormat } from '../../../../scripts/date.time.format.script';
import { accumulatedAmount, isVatSubject } from '../../../../scripts/financial.calculation.script';
import { colorStatusInvoice, colorStatusPayment, colorValidated, colorValidUntil } from '../../../../scripts/badge.color.script';
import { nameGroup } from '../../../../libraries/group.name.library';
import { statusDocumentValue } from '../../../../libraries/status.document.library';
import { validityBoolean } from '../../../../libraries/validation.library';
import { getError } from '../../../../languages/translations/response';
import { sortColumn } from '../../../../scripts/table.script';
import { errorWebHandler } from '../../../../scripts/error.web.handler.script';
import { usePaginationUrl } from '../../../../hooks/usePaginationUrl';
import { fetchDocumentPdf } from '../../../../scripts/document.pdf.script';
import { usePdfViewer } from '../../../../contexts/PdfViewerContext';
import { sanitizeFilename } from '../../../../scripts/document.filename.script';

import AppSaleListHeader from '../component/SaleListHeader';
import AppSortTable from '../../../../components/element/SortTable';
import AppPagination from '../../../../components/element/Pagination';
import AppPreloader from '../../../../components/Preloader';
import ButtonVouchers from '../../../../components/button/ButtonVouchers';

let errorResponse: Error, invoicesResponse: CompanyInvoices;

export interface InvoiceListPageProps {};

const InvoiceListPage: React.FunctionComponent<InvoiceListPageProps> = props => {
  const {setRoute, companyForUser, settingsForCompany, contactsForCompany} = UseOutletContextInvoice()
  const {lang} = StateLanguage()
  const { page: urlPage, setPage: setUrlPage } = usePaginationUrl()
  const { openUrl } = usePdfViewer();
  const vatSubject = isVatSubject(settingsForCompany, companyForUser);
  // Nombre de colonnes AVANT le bloc montants (payé/dette/TVA/total). La colonne TVA fait partie du bloc montants.
  const COLS_BEFORE_AMOUNTS = 7;
  const [pdfLoading, setPdfLoading] = useState<string | null>(null);

  const [mounted, setMounted] = useState(false)
  const [button, setButton] = useState({filter: false, group: false})
  const [filterState, setFilterState] = useState({interval: '', start_date: '', end_date: '', contact: '', contact_label: '', validity: '', status: '', expiration: '', payment: '', sort_field: '', sort_mode: '', group: '', search: ''})
  const filterRef = useRef(filterState)
  const filter = filterRef.current
  const setFilter = (v: React.SetStateAction<typeof filterState>) => { const n = typeof v === 'function' ? v(filterRef.current) : v; filterRef.current = n; setFilterState(n) }
  const [pagination, setPagination] = useState({current: 1, limit: 20, total: 0, pages: [1] as number[]})
  const [invoices, setInvoices] = useState<Invoice[] | null>(null)
  const [invoiceGroups, setInvoiceGroups] = useState<InvoiceGroup[] | null>(null)
  const [storage, setStorage] = useState(null)

  const loadInvoices = async (id_company: string, page: number = 1) => {
    const f = filterRef.current
    await InvoiceDA.getInvoices(id_company, f.interval, f.start_date, f.end_date, f.contact, f.validity, f.status, f.expiration, f.payment, f.sort_field, f.sort_mode, f.group, page, pagination.limit, f.search).then( (response) => {
      if (response.status === 200) {
        invoicesResponse = response.data

        const filterData = {
          company_id: id_company,
          interval: f.interval,
          start_date: f.start_date,
          end_date: f.end_date,
          contact: f.contact,
          contact_label: f.contact_label,
          validity: f.validity,
          status: f.status,
          expiration: f.expiration,
          payment: f.payment,
          sort_field: f.sort_field,
          sort_mode: f.sort_mode,
          group: f.group,
          search: f.search
        }
        localStorage.setItem('filter_invoices', JSON.stringify(filterData))

        const groupBy = filterData.group
        const items = invoicesResponse.items ?? invoicesResponse.invoices ?? []
        const groups = invoicesResponse.groups ?? []

        localStorage.setItem('nav_invoices', JSON.stringify({ company_id: id_company, ids: items.map((item: any) => item.id) }))

        if (!groupBy || groupBy.length === 0) {
          setInvoiceGroups([])
          setInvoices(items)

          const paginationData = invoicesResponse.pagination
          if (paginationData) {
            const totalPages = paginationData.pages || 1
            const clampedPage = page > totalPages && totalPages >= 1 ? totalPages : paginationData.page || page
            let pagesOfPagination: number[] = []
            for (let i = 1; i <= totalPages; i++) { pagesOfPagination.push(i) }
            setPagination({...pagination, current: clampedPage, total: paginationData.total || 0, pages: pagesOfPagination})
            setUrlPage(clampedPage)
            if (page > totalPages && totalPages >= 1) {
              loadInvoices(id_company, totalPages)
              return
            }
          }
        } else {
          setInvoices([])
          setInvoiceGroups(groups)
          setPagination({current: 1, limit: 20, total: 0, pages: [1]})
          setUrlPage(1)
        }

        setButton({filter: Boolean(filterData.interval || filterData.start_date || filterData.end_date || filterData.contact || filterData.validity || filterData.status || filterData.expiration || filterData.payment), group: Boolean(groupBy)})
        setStorage((localStorage.getItem('filter_invoices')) ? JSON.parse(localStorage.getItem('filter_invoices')!) : null)
      } else {
        errorResponse = response.data

        GlobalSwal.fire({
          title: getError(errorResponse.code, lang.code, errorResponse.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    }).catch( (error) => {
      errorWebHandler(lang, error)
    })
  }

  const executeSortList = (item: string) => {
    if (companyForUser) {
      const newFilter = {...filterRef.current}
      sortColumn(item, newFilter)
      setFilter(newFilter)
      setUrlPage(1)
      loadInvoices(companyForUser.id, 1)
    }
  }

  const loadPagination = (current: number) => {
    if (!companyForUser) return null
    setUrlPage(current)
    loadInvoices(companyForUser.id, current)
  }

  const executeViewPdf = async (id: string, filename: string) => {
    if (!companyForUser) return;
    setPdfLoading(id);
    try {
      const url = await fetchDocumentPdf(companyForUser.id, 'invoices', id);
      openUrl(url, `${sanitizeFilename(filename)}.pdf`);
    } catch {
      // fetchDocumentPdf throws with a readable message on error
    } finally {
      setPdfLoading(null);
    }
  };

  useEffect( () => {
    let filter_invoices: any = (localStorage.getItem('filter_invoices')) ? JSON.parse(localStorage.getItem('filter_invoices')!) : null

    if (companyForUser) {
      const isMatch = filter_invoices && filter_invoices.company_id === companyForUser.id
      setFilter({
        interval: isMatch ? (filter_invoices.interval ?? '') : '',
        start_date: isMatch ? (filter_invoices.start_date ?? '') : '',
        end_date: isMatch ? (filter_invoices.end_date ?? '') : '',
        contact: isMatch ? (filter_invoices.contact ?? '') : '',
        contact_label: isMatch ? (filter_invoices.contact_label ?? '') : '',
        validity: isMatch ? (filter_invoices.validity ?? '') : '',
        status: isMatch ? (filter_invoices.status ?? '') : '',
        expiration: isMatch ? (filter_invoices.expiration ?? '') : '',
        payment: isMatch ? (filter_invoices.payment ?? '') : '',
        sort_field: isMatch ? (filter_invoices.sort_field ?? '') : '',
        sort_mode: isMatch ? (filter_invoices.sort_mode ?? '') : '',
        group: isMatch ? (filter_invoices.group ?? '') : '',
        search: isMatch ? (filter_invoices.search ?? '') : ''
      })

      setInvoices(null)
      setInvoiceGroups(null)
      setButton({filter: false, group: false})
      loadInvoices(companyForUser.id, urlPage)
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser])

  useEffect( () => {
    setMounted(true)
    setRoute({path: {root: lang.labels.invoices, branch: lang.labels.listInvoices}, company: true})

    return () => setMounted(false)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!mounted) return null

  return (
    <div className="w-100 h-100">
      { companyForUser && invoices && invoiceGroups
        ?
        <div className="card card-flush">
          <AppSaleListHeader sale={"invoice"} storage={storage} button={button} filter={filter} records={invoices} recordGroups={invoiceGroups} setFilter={setFilter} loadDocuments={loadInvoices} totalItems={!button.group ? pagination.total : undefined}></AppSaleListHeader>
          <div className="card-body pt-0">
            <div className="dataTables_wrapper">
              <div className="table-responsive">
                <table className="table align-middle table-row-dashed mb-2">
                  <thead className="border-bottom">
                    <tr className="text-start text-muted text-uppercase fw-bolder fs-7">
                      <th className="w-100px">
                        <div className="d-flex align-items-center text-nowrap cursor-pointer" onClick={() => executeSortList("created_date")}>
                          <span className="me-1">{lang.labels.documentDate}</span>
                          <AppSortTable attribute={"created_date"} field={filter.sort_field} mode={filter.sort_mode}></AppSortTable>
                        </div>
                      </th>
                      <th className="w-125px">
                        <div className="d-flex align-items-center text-nowrap cursor-pointer" onClick={() => executeSortList("invoice_number")}>
                          <span className="me-1">{lang.labels.invoiceNumber}</span>
                          <AppSortTable attribute={"invoice_number"} field={filter.sort_field} mode={filter.sort_mode}></AppSortTable>
                        </div>
                      </th>
                      <th className="min-w-150px">
                        <div className="d-flex align-items-center text-nowrap cursor-pointer" onClick={() => executeSortList("concerning")}>
                          <span className="me-1">{lang.labels.concerning}</span>
                          <AppSortTable attribute={"concerning"} field={filter.sort_field} mode={filter.sort_mode}></AppSortTable>
                        </div>
                      </th>
                      <th className="min-w-100px">
                        <div className="d-flex align-items-center text-nowrap cursor-pointer" onClick={() => executeSortList("customer")}>
                          <span className="me-1">{lang.labels.customer}</span>
                          <AppSortTable attribute={"customer"} field={filter.sort_field} mode={filter.sort_mode}></AppSortTable>
                        </div>
                      </th>
                      <th className="min-w-100px">
                        <div className="d-flex align-items-center text-nowrap cursor-pointer" onClick={() => executeSortList("validity")}>
                          <span className="me-1">{lang.labels.validity}</span>
                          <AppSortTable attribute={"validity"} field={filter.sort_field} mode={filter.sort_mode}></AppSortTable>
                        </div>
                      </th>
                      <th className="min-w-100px">
                        <div className="d-flex align-items-center text-nowrap cursor-pointer" onClick={() => executeSortList("status")}>
                          <span className="me-1">{lang.labels.status}</span>
                          <AppSortTable attribute={"status"} field={filter.sort_field} mode={filter.sort_mode}></AppSortTable>
                        </div>
                      </th>
                      <th className="min-w-100px">
                        <div className="d-flex align-items-center text-nowrap cursor-pointer" onClick={() => executeSortList("expiration")}>
                          <span className="me-1">{lang.labels.expiration}</span>
                          <AppSortTable attribute={"expiration"} field={filter.sort_field} mode={filter.sort_mode}></AppSortTable>
                        </div>
                      </th>
                      <th className="min-w-100px text-end">
                        <div className="d-flex align-items-center justify-content-end text-nowrap cursor-pointer" onClick={() => executeSortList("amount.paid")}>
                          <span className="me-1">{lang.labels.payed}</span>
                          <AppSortTable attribute={"amount.paid"} field={filter.sort_field} mode={filter.sort_mode}></AppSortTable>
                        </div>
                      </th>
                      <th className="min-w-100px text-end">
                        <div className="d-flex align-items-center justify-content-end text-nowrap cursor-pointer" onClick={() => executeSortList("amount.debt")}>
                          <span className="me-1">{lang.labels.debt}</span>
                          <AppSortTable attribute={"amount.debt"} field={filter.sort_field} mode={filter.sort_mode}></AppSortTable>
                        </div>
                      </th>
                      {vatSubject && <th className="min-w-100px text-end">
                        <div className="d-flex align-items-center justify-content-end text-nowrap cursor-pointer" onClick={() => executeSortList("amount.tax_total")}>
                          <span className="me-1">{lang.labels.vatAmount}</span>
                          <AppSortTable attribute={"amount.tax_total"} field={filter.sort_field} mode={filter.sort_mode}></AppSortTable>
                        </div>
                      </th>}
                      <th className="min-w-100px text-end">
                        <div className="d-flex align-items-center justify-content-end text-nowrap cursor-pointer" onClick={() => executeSortList("amount.total")}>
                          <span className="me-1">{lang.labels.amountTotal}</span>
                          <AppSortTable attribute={"amount.total"} field={filter.sort_field} mode={filter.sort_mode}></AppSortTable>
                        </div>
                      </th>
                      <th className="w-50px text-center"></th>
                      <th className="w-50px text-center"></th>
                    </tr>
                  </thead>
                  { invoices.length > 0 || invoiceGroups.length > 0
                    ?
                    <>
                      { button.group
                        ?
                          <>
                            { invoiceGroups.map (( (item, index) => { return (
                              <tbody key={index} className="text-secondary fw-bold">
                                <tr>
                                  <td colSpan={12 + (vatSubject ? 1 : 0)} className="text-uppercase fw-bolder fs-7">
                                    <div className="d-flex align-items-center">
                                      <span className="svg-icon svg-icon-3 svg-icon-gray-900 me-5">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                          <path d="M22 12C22 12.9 21.9 13.8 21.7 14.6L5 4.89999C6.8 3.09999 9.3 2 12 2C17.5 2 22 6.5 22 12Z" fill="black"/>
                                          <path opacity="0.3" d="M3.7 17.5C2.6 15.9 2 14 2 12C2 9.20003 3.1 6.70002 5 4.90002L9.3 7.40002V14.2L3.7 17.5ZM17.2 12L5 19.1C6.8 20.9 9.3 22 12 22C16.6 22 20.5 18.8 21.7 14.6L17.2 12Z" fill="black"/>
                                        </svg>
                                      </span>
                                      <span className="me-3">{lang.labels.group} {index + 1}:</span>
                                      {nameGroup(lang, filter.group, item.group_detail)}
                                    </div>
                                  </td>
                                </tr>
                                { item.invoices.map (( (sub_item, sub_index) => { return (
                                  <tr key={sub_index} className="row-dashed">
                                    <td className="text-nowrap">{dateFormat(lang.code, sub_item.created_date)}</td>
                                    <td>
                                      <Link to={"/main/sale/invoice/view/" + sub_item.id} className="fw-bolder text-nowrap text-dark text-hover-primary">{sub_item.invoice_number}</Link>
                                    </td>
                                    <td>{sub_item.concerning}</td>
                                    <td>{formatContact(sub_item.customer)}</td>
                                    <td>
                                      <span className={`badge badge-light-${colorValidated(sub_item.validated)} fs-7`}>{validityBoolean(lang, sub_item.validated)}</span>
                                    </td>
                                    <td>
                                      <span className={`badge badge-light-${colorStatusInvoice(sub_item.status)} fs-7`}>{statusDocumentValue(lang, sub_item.status)}</span>
                                    </td>
                                    <td>
                                      <span className={`badge badge-light-${colorValidUntil(sub_item.status, sub_item.valid_until)} fs-7`}>{dateFormat(lang.code, sub_item.valid_until)}</span>
                                    </td>
                                    <td className="text-end">
                                      <span className={`badge badge-light-${colorStatusPayment(sub_item.payment_status)} fs-7`}>{sub_item.currency.code} {formatMoney(sub_item.amount.paid)}</span>
                                    </td>
                                    <td className="text-end">
                                      <span className={`badge badge-light-${colorStatusPayment(sub_item.payment_status)} fs-7`}>{sub_item.currency.code} {formatMoney(sub_item.amount.debt)}</span>
                                    </td>
                                    {vatSubject && <td className="text-end text-nowrap">{sub_item.currency.code} {formatMoney(sub_item.amount?.tax_total ?? 0)}</td>}
                                    <td className="text-end text-nowrap">
                                      {sub_item.currency.code} {formatMoney(sub_item.amount.total)}
                                      {sub_item.foreign_currency?.code && <div className="fs-9 fw-normal text-muted">{sub_item.foreign_currency.code} {formatMoney(sub_item.foreign_currency.amount?.total ?? 0)}</div>}
                                    </td>
                                    <td className="text-center">
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-icon btn-light-primary"
                                        title={lang.labels.view}
                                        onClick={() => executeViewPdf(sub_item.id, sub_item.invoice_number)}
                                        disabled={pdfLoading === sub_item.id}
                                      >
                                        {pdfLoading === sub_item.id
                                          ? <span className="spinner-border spinner-border-sm"></span>
                                          : <i className="bi bi-eye fs-5"></i>
                                        }
                                      </button>
                                    </td>
                                    <td className="text-center">
                                      <ButtonVouchers vouchers={sub_item.vouchers} />
                                    </td>
                                  </tr>
                                )}))}
                                <tr className={`fw-bolder fs-7 ${index < invoiceGroups.length - 1 && "row-dashed"}`}>
                                  <td colSpan={COLS_BEFORE_AMOUNTS}></td>
                                  <td className="text-end text-nowrap">{companyForUser.currency?.code} {formatMoney(accumulatedAmount(item.invoices).paid)}</td>
                                  <td className="text-end text-nowrap">{companyForUser.currency?.code} {formatMoney(accumulatedAmount(item.invoices).debt)}</td>
                                  {vatSubject && <td className="text-end text-nowrap">{companyForUser.currency?.code} {formatMoney(accumulatedAmount(item.invoices).tax)}</td>}
                                  <td className="text-end text-nowrap">{companyForUser.currency?.code} {formatMoney(accumulatedAmount(item.invoices).total)}</td>
                                  <td></td>
                                  <td></td>
                                </tr>
                              </tbody>
                            )}))}
                          </>
                        :
                        <>
                          <tbody className="text-secondary fw-bold">
                            { invoices.map (( (item, index) => { return (
                              <tr key={index} className="row-dashed">
                                <td className="text-nowrap">{dateFormat(lang.code, item.created_date)}</td>
                                <td>
                                  <Link to={"/main/sale/invoice/view/" + item.id} className="fw-bolder text-nowrap text-dark text-hover-primary">{item.invoice_number}</Link>
                                </td>
                                <td>{item.concerning}</td>
                                <td>{formatContact(item.customer)}</td>
                                <td>
                                  <span className={`badge badge-light-${colorValidated(item.validated)} fs-7`}>{validityBoolean(lang, item.validated)}</span>
                                </td>
                                <td>
                                  <span className={`badge badge-light-${colorStatusInvoice(item.status)} fs-7`}>{statusDocumentValue(lang, item.status)}</span>
                                </td>
                                <td>
                                  <span className={`badge badge-light-${colorValidUntil(item.status, item.valid_until)} fs-7`}>{dateFormat(lang.code, item.valid_until)}</span>
                                </td>
                                <td className="text-end">
                                  <span className={`badge badge-light-${colorStatusPayment(item.payment_status)} fs-7`}>{item.currency.code} {formatMoney(item.amount.paid)}</span>
                                </td>
                                <td className="text-end">
                                  <span className={`badge badge-light-${colorStatusPayment(item.payment_status)} fs-7`}>{item.currency.code} {formatMoney(item.amount.debt)}</span>
                                </td>
                                {vatSubject && <td className="text-end text-nowrap">{item.currency.code} {formatMoney(item.amount?.tax_total ?? 0)}</td>}
                                <td className="text-end text-nowrap">
                                  {item.currency.code} {formatMoney(item.amount.total)}
                                  {item.foreign_currency?.code && <div className="fs-9 fw-normal text-muted">{item.foreign_currency.code} {formatMoney(item.foreign_currency.amount?.total ?? 0)}</div>}
                                </td>
                                <td className="text-center">
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-icon btn-light-primary"
                                    title={lang.labels.view}
                                    onClick={() => executeViewPdf(item.id, item.invoice_number)}
                                    disabled={pdfLoading === item.id}
                                  >
                                    {pdfLoading === item.id
                                      ? <span className="spinner-border spinner-border-sm"></span>
                                      : <i className="bi bi-eye fs-5"></i>
                                    }
                                  </button>
                                </td>
                                <td className="text-center">
                                  <ButtonVouchers vouchers={item.vouchers} />
                                </td>
                              </tr>
                            )}))}
                          </tbody>
                          <tfoot>
                            <tr className="fw-bolder fs-7">
                              <td colSpan={COLS_BEFORE_AMOUNTS}></td>
                              <td className="text-end text-nowrap">{companyForUser.currency?.code} {formatMoney(accumulatedAmount(invoices).paid)}</td>
                              <td className="text-end text-nowrap">{companyForUser.currency?.code} {formatMoney(accumulatedAmount(invoices).debt)}</td>
                              {vatSubject && <td className="text-end text-nowrap">{companyForUser.currency?.code} {formatMoney(accumulatedAmount(invoices).tax)}</td>}
                              <td className="text-end text-nowrap">{companyForUser.currency?.code} {formatMoney(accumulatedAmount(invoices).total)}</td>
                              <td></td>
                              <td></td>
                            </tr>
                          </tfoot>
                        </>
                      }
                    </>
                    :
                    <tbody>
                      <tr className="text-secondary fw-bold">
                        <td colSpan={12 + (vatSubject ? 1 : 0)} className="text-center">{lang.labels.noRecordsFound}</td>
                      </tr>
                    </tbody>
                  }
                </table>
              </div>
              {!button.group && <AppPagination showOption={false} pagination={pagination} loadPagination={loadPagination}></AppPagination>}
            </div>
          </div>
        </div>
        :
        <AppPreloader></AppPreloader>
      }
    </div>
  )
}

export default InvoiceListPage;
